/**
 * /tenant-records/$slug/administrators — the platform's administration of
 * who runs a tenant. Platform controls (platform_admins.read to see,
 * platform_admins.invite for links and invitations, platform_admins.manage
 * for accepted administrators), so no tenant grant is needed; the parent
 * tenant-record route already requires a platform admin with tenants.read.
 * The loader prefetches the roster and the RBAC catalog (immutable per
 * deploy) that decides which roles an administrator can be moved to. Thin
 * route shim; the page lives in features/tenants/.
 */

import { createFileRoute } from "@tanstack/react-router";
import { ForbiddenError } from "@/lib/api-client";
import { sessionHasPermission, sessionQueryKey } from "@/queries/session";
import type { AdminSession } from "@/queries/session";
import { queryKeys } from "@/lib/query-keys";
import { fetchRBACCatalog } from "@/features/platform-admins/rbac-queries";
import { tenantAdminCensusOptions } from "@/features/tenants/queries";
import { TenantRecordAdministratorsPage } from "@/features/tenants/pages/tenant-record-administrators-page";

export const Route = createFileRoute("/_authed/tenant-records/$slug/administrators/")({
  beforeLoad: ({ context }) => {
    if (!sessionHasPermission(context.queryClient.getQueryData<AdminSession>(sessionQueryKey), "platform_admins.read")) {
      throw new ForbiddenError(null, null, "Your platform role does not include administrator records.");
    }
  },
  loader: async ({ context }) => {
    if (context.record.is_infrastructure) return null;
    // Prefetch failures render in the page's own states, not the route boundary.
    await Promise.all([
      context.queryClient.ensureInfiniteQueryData(tenantAdminCensusOptions(context.record.id)).catch(() => undefined),
      context.queryClient.ensureQueryData({
        queryKey: queryKeys.rbacCatalog,
        queryFn: fetchRBACCatalog,
        staleTime: Infinity,
      }).catch(() => undefined),
    ]);
    return null;
  },
  component: TenantRecordAdministratorsPage,
});
